<template>
  <CheckoutStep>
    <template #header>
      <Stepper :has-bouygues-steps="hasSelectedMobilePlan(items)" />
    </template>
    <template #aside>
      <CheckoutSummary
        :discount
        :discount-token
        :items="getAvailableItems(items)"
        :selected-payment-method="paymentMethod"
        :summary
        :swapOffer
        with-extended-legal
      />
    </template>

    <div aria-live="polite" role="status">
      <BillingAddress
        :address-list
        :billable-countries
        :items
        :loading
        :swap-offer
        @update="handleUpdate"
      />
    </div>
  </CheckoutStep>
</template>

<script setup lang="ts">
import { navigateTo } from '#imports'
import { computed, onMounted } from 'vue'

import type { BuybackOffer } from '@backmarket/http-api'
import type { PaymentMethod } from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'

import CheckoutStep from '~/scopes/checkout/components/CheckoutStep/CheckoutStep.vue'
import CheckoutSummary from '~/scopes/checkout/components/CheckoutSummary/CheckoutSummary.vue'
import { useRedirectIfCartEmpty } from '~/scopes/checkout/composables/redirects/useRedirectIfCartEmpty'
import { useCheckoutTracking } from '~/scopes/checkout/composables/useCheckoutTracking'
import { CHECKOUT } from '~/scopes/checkout/routes-names'
import type { Discount } from '~/scopes/checkout/types/discount'
import { getAvailableItems } from '~/scopes/checkout/utils/cart'
import { hasSelectedMobilePlan } from '~/scopes/checkout/utils/hasSelectedMobilePlan'

import Stepper from '../../components/Stepper/Stepper.vue'

import BillingAddress from './components/BillingAddress/BillingAddress.vue'

const props = defineProps<{
  loading: boolean
  items: ApiSpecs['/bm/cart']['GET']['response']['cartItems']
  addressList: ApiSpecs['/bm/cart']['GET']['response']['addressList']
  billableCountries: ApiSpecs['/bm/cart']['GET']['response']['billableCountries']
  summary: ApiSpecs['/bm/cart']['GET']['response']['summary']
  swapOffer: BuybackOffer | null
  discount: Discount
  discountToken: string
  paymentMethod: PaymentMethod | null
}>()

const emit = defineEmits<{
  (e: 'update', callback: () => void): void
}>()

const { trackFunnel } = useCheckoutTracking()
const { redirectIfCartEmpty } = useRedirectIfCartEmpty('BILLING_ADDRESS')
const experiments = useExperiments()

const isSinglePageAddressesEnabled = computed(() => {
  return experiments['experiment.checkoutOnePageAddress'] === 'onePageAddress'
})

onMounted(() => {
  if (isSinglePageAddressesEnabled.value) {
    navigateTo({ name: CHECKOUT.ADDRESSES })

    return
  }

  redirectIfCartEmpty(props.items, CHECKOUT.CART)
  trackFunnel(props.items, props.swapOffer)
})

async function handleUpdate() {
  emit('update', async () => {
    await navigateTo({
      name: CHECKOUT.ADDRESS_CONFIRMATION,
    })
  })
}
</script>
